/**
 * Transaction filters panel
 */
import { useState, useCallback, useMemo } from 'react';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { useAccounts } from '@/hooks';
import CategorySelect from './CategorySelect';
import { cn } from '@/utils/cn';
import type { TransactionFilters as TFilters } from '@/types';

export interface TransactionFiltersProps {
  filters: TFilters;
  onChange: (filters: TFilters) => void;
  onReset: () => void;
  className?: string;
}

type DatePreset = 'all' | 'today' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'custom';

/**
 * Derive date preset from existing filter dates
 */
function derivePresetFromDates(
  startDate: string | undefined,
  endDate: string | undefined
): DatePreset {
  if (!startDate || !endDate) return 'all';

  const today = new Date();
  const start = new Date(startDate + 'T00:00:00');  // Parse as local
  const end = new Date(endDate + 'T00:00:00');

  // Check if matches "This Month"
  const thisMonthStart = startOfMonth(today);
  const thisMonthEnd = endOfMonth(today);
  if (
    format(start, 'yyyy-MM-dd') === format(thisMonthStart, 'yyyy-MM-dd') &&
    format(end, 'yyyy-MM-dd') === format(thisMonthEnd, 'yyyy-MM-dd')
  ) {
    return 'thisMonth';
  }

  // Check if matches "Last Month"
  const lastMonth = subMonths(today, 1);
  const lastMonthStart = startOfMonth(lastMonth);
  const lastMonthEnd = endOfMonth(lastMonth);
  if (
    format(start, 'yyyy-MM-dd') === format(lastMonthStart, 'yyyy-MM-dd') &&
    format(end, 'yyyy-MM-dd') === format(lastMonthEnd, 'yyyy-MM-dd')
  ) {
    return 'lastMonth';
  }

  // Dates are set but don't match a preset - show custom
  return 'custom';
}

/**
 * Check if any filters are active
 */
function hasActiveFilters(filters: TFilters): boolean {
  return Object.values(filters).some((v) => v !== undefined && v !== '');
}

/**
 * Filter panel for transactions
 */
export default function TransactionFilters({
  filters,
  onChange,
  onReset,
  className,
}: TransactionFiltersProps) {
  // Auto-expand when filters are pre-populated (e.g., from URL params)
  // Only compute on mount - we don't want to collapse/expand based on filter changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialHasFilters = useMemo(() => hasActiveFilters(filters), []);
  const [isExpanded, setIsExpanded] = useState(initialHasFilters);

  // Derive the initial date preset from pre-populated filter dates
  const initialDatePreset = useMemo(
    () => derivePresetFromDates(filters.startDate, filters.endDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []  // Only compute on mount
  );
  const [datePreset, setDatePreset] = useState<DatePreset>(initialDatePreset);

  const { data: accounts = [] } = useAccounts();

  const handleFilterChange = useCallback(
    (key: keyof TFilters, value: TFilters[keyof TFilters]) => {
      onChange({
        ...filters,
        [key]: value === '' ? undefined : value,
      });
    },
    [filters, onChange]
  );

  const handleDatePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    const today = new Date();

    let startDate: string | undefined;
    let endDate: string | undefined;

    switch (preset) {
      case 'today':
        startDate = format(today, 'yyyy-MM-dd');
        endDate = format(today, 'yyyy-MM-dd');
        break;
      case 'last7':
        startDate = format(subDays(today, 7), 'yyyy-MM-dd');
        endDate = format(today, 'yyyy-MM-dd');
        break;
      case 'last30':
        startDate = format(subDays(today, 30), 'yyyy-MM-dd');
        endDate = format(today, 'yyyy-MM-dd');
        break;
      case 'thisMonth':
        startDate = format(startOfMonth(today), 'yyyy-MM-dd');
        endDate = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'lastMonth': {
        const lastMonth = subMonths(today, 1);
        startDate = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
        endDate = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
        break;
      }
      case 'all':
      case 'custom':
      default:
        startDate = undefined;
        endDate = undefined;
    }

    onChange({
      ...filters,
      startDate,
      endDate,
    });
  };

  const handleReset = () => {
    setDatePreset('all');
    onReset();
  };

  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== undefined && v !== ''
  ).length;

  return (
    <div className={cn('bg-white border border-gray-200 rounded-lg', className)}>
      {/* Filter toggle header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
        aria-expanded={isExpanded}
      >
        <span className="flex items-center text-sm font-medium text-gray-700">
          <svg
            className="h-5 w-5 mr-2 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
              clipRule="evenodd"
            />
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </span>
        <svg
          className={cn(
            'h-5 w-5 text-gray-400 transition-transform',
            isExpanded && 'transform rotate-180'
          )}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Expanded filter options */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-200 pt-4 space-y-4">
          {/* Date presets */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'all', label: 'All Time' },
                { value: 'today', label: 'Today' },
                { value: 'last7', label: 'Last 7 Days' },
                { value: 'last30', label: 'Last 30 Days' },
                { value: 'thisMonth', label: 'This Month' },
                { value: 'lastMonth', label: 'Last Month' },
              ].map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => handleDatePresetChange(preset.value as DatePreset)}
                  className={cn(
                    'px-3 py-1.5 text-sm rounded-full border transition-colors',
                    datePreset === preset.value
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Custom date inputs */}
            {datePreset === 'custom' && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="start-date"
                    className="block text-xs font-medium text-gray-500 mb-1"
                  >
                    From
                  </label>
                  <input
                    type="date"
                    id="start-date"
                    value={filters.startDate || ''}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="end-date"
                    className="block text-xs font-medium text-gray-500 mb-1"
                  >
                    To
                  </label>
                  <input
                    type="date"
                    id="end-date"
                    value={filters.endDate || ''}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Account filter */}
          <div>
            <label
              htmlFor="account-filter"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Account
            </label>
            <select
              id="account-filter"
              value={filters.accountId || ''}
              onChange={(e) => handleFilterChange('accountId', e.target.value)}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Accounts</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                  {account.mask ? ` (***${account.mask})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Category filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <CategorySelect
              value={filters.categoryId}
              onChange={(value) => handleFilterChange('categoryId', value)}
              placeholder="All Categories"
            />
          </div>

          {/* Transaction type filter */}
          <div>
            <label
              htmlFor="type-filter"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Type
            </label>
            <select
              id="type-filter"
              value={filters.type || ''}
              onChange={(e) =>
                handleFilterChange('type', e.target.value as TFilters['type'])
              }
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Types</option>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
              <option value="transfer">Transfer</option>
            </select>
          </div>

          {/* Amount range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount Range
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="min-amount"
                  className="block text-xs font-medium text-gray-500 mb-1"
                >
                  Min
                </label>
                <input
                  type="number"
                  id="min-amount"
                  value={filters.minAmount ?? ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'minAmount',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <label
                  htmlFor="max-amount"
                  className="block text-xs font-medium text-gray-500 mb-1"
                >
                  Max
                </label>
                <input
                  type="number"
                  id="max-amount"
                  value={filters.maxAmount ?? ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'maxAmount',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="No limit"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Reset button */}
          {activeFilterCount > 0 && (
            <div className="pt-2 border-t border-gray-200">
              <button
                type="button"
                onClick={handleReset}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Reset all filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
